/**
 * Story hero: article header + video + news-style overlay — all driven by config.
 *
 * Template usage:
 *   1) Add an empty mount: <div id="story-hero-root"></div> (inside your article if you want).
 *   2) Before this script, set window.STORY_HERO_CONFIG (see defaults below).
 *   3) Load this file after your HTML: <script src="story-hero-dynamic.js" defer></script>
 *      (defer keeps order: config script first, then this file.)
 *
 * Optional: call window.mountStoryHero(customConfig) after changing config (e.g. Shopify section editor).
 */
(function () {
  "use strict";

  var DEFAULT_CONFIG = {
    article: {
      /** If false, only the video block is rendered. */
      show: true,
      /** Full HTML inside <h1> (use <span class="marker-highlight">…</span> for yellow markers). */
      titleHtml:
        'A <span class="marker-highlight">$10 Remineralizing Gum</span> Helped a 34-Year-Old With Coffee-Stained, Chalky Teeth <span class="marker-highlight">Save Thousands</span> in Dental Work.',
      subtitle: "Her dentist had no explanation.",
      authorInitials: "JM",
      authorName: "Jessica Monroe, Dental Health Writer",
      published: "April 2026",
      viewsLabel: "486,385 views",
    },
    video: {
      src: "https://cdn.shopify.com/videos/c/o/v/5dc8ec4b8ce3404484007625cf19bee0.mp4",
      ariaLabel:
        "Special Report: news anchor presents the oral health crisis story",
      autoplay: true,
      loop: true,
      muted: true,
      playsInline: true,
    },
    overlay: {
      /** Shown in red badge; use array for two lines (inserts <br>) or one string. */
      breakingLabel: ["Breaking", "News"],
      /**
       * String OR array of strings. If both headline and quote are arrays, they rotate in sync (index 0 with 0, …).
       * If lengths differ, each list cycles with its own modulus (pair index advances every rotateMs).
       */
      headline: [
        "40-year-old Japanese technology shown to help support tooth enamel",
        "The quiet oral health crisis affecting millions of Americans",
        "How one daily habit is reshaping adult oral care",
        "Dentists stunned: 34-year-old woman chooses gum for healthier teeth",
      ],
      liveLabel: "Live · Now",
      quote: [
        "Researchers point to nano-hydroxyapatite as a breakthrough ingredient",
        "Adults under 40 report rising sensitivity, dullness, and dry mouth",
        "Dentists call it the easiest oral care swap of the decade",
        '"I haven\'t felt this confident about my smile in years" — verified buyer',
      ],
      /** Ms between headline/quote changes; set 0 to disable rotation. */
      rotateMs: 7000,
      /**
       * Clock in the ticker: "auto" = visitor's local time + short TZ (browser).
       * Any other string is shown as-is (e.g. "04:59 pm ET" for a fixed Eastern time).
       */
      timeLocal: "auto",
    },
  };

  /** e.g. "4:59 PM EST" or locale-appropriate, using the user's system timezone */
  function formatViewerLocalTime() {
    var now = new Date();
    try {
      return new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "short",
      }).format(now);
    } catch (e) {
      return now.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    }
  }

  function resolveOverlayTime(raw) {
    if (raw === "auto" || raw === null || raw === undefined) {
      return formatViewerLocalTime();
    }
    return String(raw);
  }

  /** One string -> [string]; arrays kept as string list */
  function toStringArray(val) {
    if (Array.isArray(val)) {
      return val.map(function (x) {
        return x == null ? "" : String(x);
      });
    }
    return [val == null || val === "" ? "" : String(val)];
  }

  var TICKER_STYLE_ID = "story-hero-ticker-animations";

  /** Fade + slight slide up on each headline/quote change (works without Tailwind). */
  function ensureTickerStyles() {
    if (document.getElementById(TICKER_STYLE_ID)) return;
    var style = document.createElement("style");
    style.id = TICKER_STYLE_ID;
    style.textContent =
      "@keyframes story-hero-ticker-fade-up{0%{opacity:0;transform:translate3d(0,10px,0)}to{opacity:1;transform:translate3d(0,0,0)}}" +
      "@keyframes story-hero-ticker-fade-only{0%{opacity:0}to{opacity:1}}" +
      ".story-hero-ticker-anim{animation:story-hero-ticker-fade-up .45s cubic-bezier(.22,1,.36,1) both}" +
      "@media (prefers-reduced-motion:reduce){.story-hero-ticker-anim{animation:story-hero-ticker-fade-only .22s ease-out both}}";

    document.head.appendChild(style);
  }

  /**
   * Replace text and replay enter animation (remove class → reflow → set text → add class).
   */
  function refreshTickerAnimatedLine(el, text) {
    ensureTickerStyles();
    if (!el) return;
    el.classList.remove("story-hero-ticker-anim");
    void el.offsetWidth;
    if (text !== undefined && text !== null) {
      el.textContent = text;
    }
    el.classList.add("story-hero-ticker-anim");
  }

  var clearStoryHeroTicker = null;

  function mergeDeep(base, patch) {
    if (!patch || typeof patch !== "object") return base;
    var out = {};
    var k;
    for (k in base) {
      if (!Object.prototype.hasOwnProperty.call(base, k)) continue;
      var bv = base[k];
      var pv = patch[k];
      if (Array.isArray(bv)) {
        out[k] = pv !== undefined ? pv : bv.slice();
      } else if (
        bv !== null &&
        typeof bv === "object" &&
        !Array.isArray(bv) &&
        pv !== undefined &&
        typeof pv === "object" &&
        !Array.isArray(pv)
      ) {
        out[k] = mergeDeep(bv, pv);
      } else if (pv !== undefined) {
        out[k] = pv;
      } else {
        out[k] = bv;
      }
    }
    for (k in patch) {
      if (
        Object.prototype.hasOwnProperty.call(patch, k) &&
        !Object.prototype.hasOwnProperty.call(out, k)
      ) {
        out[k] = patch[k];
      }
    }
    return out;
  }

  /**
   * Cycles headline + quote in sync. Each list can be shorter; values wrap (paired index advances every rotateMs).
   * When timeLocal is "auto", the clock text refreshes on each step.
   */
  function startOverlayRotation(figure, o) {
    var hArr = toStringArray(o.headline);
    var qArr = toStringArray(o.quote);
    if (!hArr.length) hArr = [""];
    if (!qArr.length) qArr = [""];
    var n = Math.max(hArr.length, qArr.length, 1);
    var rotateMs = o.rotateMs;
    if (rotateMs === false || rotateMs === 0 || n <= 1) {
      return function noop() {};
    }
    if (typeof rotateMs !== "number" || rotateMs < 0) {
      rotateMs = 7000;
    }

    var headlineP = figure.querySelector('[data-story-ticker="headline"]');
    var quoteP = figure.querySelector('[data-story-ticker="quote"]');
    var timeCol = figure.querySelector('[data-story-ticker="time"]');
    if (!headlineP || !quoteP) {
      return function noop() {};
    }

    var i = 0;

    function tick() {
      i = (i + 1) % n;
      refreshTickerAnimatedLine(
        headlineP,
        hArr[i % hArr.length] || ""
      );
      refreshTickerAnimatedLine(quoteP, qArr[i % qArr.length] || "");
      if (
        timeCol &&
        (o.timeLocal === "auto" ||
          o.timeLocal === null ||
          o.timeLocal === undefined)
      ) {
        timeCol.textContent = resolveOverlayTime("auto");
      }
    }

    var id = setInterval(tick, rotateMs);
    return function clear() {
      clearInterval(id);
    };
  }

  function buildBreakingBadge(lines) {
    var wrap = document.createElement("div");
    wrap.className =
      "bg-news-red text-paper px-3 sm:px-5 py-2 sm:py-3 flex items-center font-sans-ed font-black tracking-[0.18em] uppercase text-[11px] sm:text-sm leading-tight h-full shadow-md";
    if (Array.isArray(lines)) {
      lines.forEach(function (line, i) {
        wrap.appendChild(document.createTextNode(line));
        if (i < lines.length - 1) wrap.appendChild(document.createElement("br"));
      });
    } else {
      wrap.textContent = String(lines || "");
    }
    return wrap;
  }

  function buildArticleHeader(a) {
    var header = document.createElement("header");
    header.className = "mb-6";

    var h1 = document.createElement("h1");
    h1.className =
      "font-display text-4xl sm:text-5xl md:text-6xl font-black leading-[0.95] text-ink text-balance";
    h1.innerHTML = a.titleHtml || "";

    var sub = document.createElement("p");
    sub.className =
      "mt-4 font-display text-xl sm:text-2xl text-muted-foreground italic text-balance";
    sub.textContent = a.subtitle || "";

    var meta = document.createElement("div");
    meta.className =
      "mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-sans-ed text-muted-foreground border-y border-rule py-3";

    var authorSpan = document.createElement("span");
    authorSpan.className = "flex items-center gap-2";
    var avatar = document.createElement("span");
    avatar.className =
      "w-7 h-7 rounded-full bg-turquoise grid place-items-center text-paper font-bold text-[11px]";
    avatar.textContent = a.authorInitials || "";
    authorSpan.appendChild(avatar);
    authorSpan.appendChild(document.createTextNode("By "));
    var authorStrong = document.createElement("span");
    authorStrong.className = "text-ink font-semibold";
    authorStrong.textContent = a.authorName || "";
    authorSpan.appendChild(authorStrong);

    var dateSpan = document.createElement("span");
    dateSpan.textContent = a.published || "";

    var viewsSpan = document.createElement("span");
    viewsSpan.className = "flex items-center gap-1";
    viewsSpan.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye h-3.5 w-3.5"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></svg> ';
    viewsSpan.appendChild(document.createTextNode(a.viewsLabel || ""));

    var iconsSpan = document.createElement("span");
    iconsSpan.className = "ml-auto flex items-center gap-3";
    iconsSpan.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-share2 h-4 w-4"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line></svg><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bookmark h-4 w-4"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"></path></svg>';

    meta.appendChild(authorSpan);
    meta.appendChild(dateSpan);
    meta.appendChild(viewsSpan);
    meta.appendChild(iconsSpan);

    header.appendChild(h1);
    header.appendChild(sub);
    header.appendChild(meta);
    return header;
  }

  function buildVideoFigure(cfg) {
    var v = cfg.video;
    var o = cfg.overlay;
    var hArr = toStringArray(o.headline);
    var qArr = toStringArray(o.quote);
    if (!hArr.length) hArr = [""];
    if (!qArr.length) qArr = [""];

    var figure = document.createElement("figure");
    figure.className = "relative my-8 group";

    var outer = document.createElement("div");
    outer.className =
      "relative overflow-hidden border-2 border-ink shadow-editorial bg-ink";

    var video = document.createElement("video");
    video.className = "w-full aspect-video object-cover";
    if (v.src) video.src = v.src;
    if (v.ariaLabel) video.setAttribute("aria-label", v.ariaLabel);
    video.autoplay = !!v.autoplay;
    video.loop = !!v.loop;
    video.muted = !!v.muted;
    video.playsInline = !!v.playsInline;
    video.defaultMuted = !!v.muted;

    var overlayRoot = document.createElement("div");
    overlayRoot.className = "absolute bottom-0 inset-x-0";

    var borderWrap = document.createElement("div");
    borderWrap.className = "border-t-2 border-news-red";

    var row1 = document.createElement("div");
    row1.className =
      "flex items-stretch bg-paper/95 backdrop-blur relative";

    var badgeCol = document.createElement("div");
    badgeCol.className = "relative shrink-0";
    badgeCol.appendChild(buildBreakingBadge(o.breakingLabel));
    var skew = document.createElement("div");
    skew.className =
      "absolute top-0 -right-2 h-full w-2 bg-highlight skew-x-[-12deg]";
    badgeCol.appendChild(skew);

    var headlineCol = document.createElement("div");
    headlineCol.className =
      "flex-1 pl-5 sm:pl-7 pr-3 sm:pr-5 py-2 sm:py-3 flex items-center min-w-0 overflow-hidden bg-gradient-to-r from-paper to-paper/80";
    var headlineP = document.createElement("p");
    headlineP.setAttribute("data-story-ticker", "headline");
    headlineP.className =
      "font-sans-ed font-extrabold text-ink text-sm sm:text-lg leading-tight uppercase tracking-tight truncate sm:whitespace-normal";
    headlineP.textContent = hArr[0] || "";
    headlineCol.appendChild(headlineP);

    row1.appendChild(badgeCol);
    row1.appendChild(headlineCol);

    var row2 = document.createElement("div");
    row2.className =
      "flex items-stretch bg-ink/90 border-t border-news-red/60";

    var live = document.createElement("div");
    live.className =
      "bg-highlight text-ink px-3 sm:px-5 py-1 sm:py-1.5 font-sans-ed font-bold tracking-[0.2em] uppercase text-[9px] sm:text-[10px] flex items-center shrink-0";
    live.textContent = o.liveLabel || "";

    var quoteCol = document.createElement("div");
    quoteCol.className =
      "flex-1 px-3 sm:px-5 py-1 sm:py-1.5 flex items-center min-w-0 overflow-hidden";
    var quoteP = document.createElement("p");
    quoteP.setAttribute("data-story-ticker", "quote");
    quoteP.className =
      "font-sans-ed text-paper text-[11px] sm:text-sm leading-tight italic truncate";
    quoteP.textContent = qArr[0] || "";
    quoteCol.appendChild(quoteP);

    var timeCol = document.createElement("div");
    timeCol.setAttribute("data-story-ticker", "time");
    timeCol.className =
      "hidden sm:flex items-center px-3 font-mono-ed text-[10px] text-paper/70 border-l border-paper/20";
    timeCol.textContent = resolveOverlayTime(o.timeLocal);

    row2.appendChild(live);
    row2.appendChild(quoteCol);
    row2.appendChild(timeCol);

    borderWrap.appendChild(row1);
    borderWrap.appendChild(row2);
    overlayRoot.appendChild(borderWrap);

    outer.appendChild(video);
    outer.appendChild(overlayRoot);
    figure.appendChild(outer);

    if (v.autoplay) {
      video.play().catch(function () {});
    }

    return figure;
  }

  function mountStoryHero(partial) {
    var root = document.getElementById("story-hero-root");
    if (!root) return;

    if (clearStoryHeroTicker) {
      clearStoryHeroTicker();
      clearStoryHeroTicker = null;
    }

    var cfg = mergeDeep(
      DEFAULT_CONFIG,
      mergeDeep(
        typeof window.STORY_HERO_CONFIG === "object"
          ? window.STORY_HERO_CONFIG
          : {},
        partial || {}
      )
    );

    root.innerHTML = "";
    root.removeAttribute("data-story-hero-mounted");

    if (cfg.article && cfg.article.show) {
      root.appendChild(buildArticleHeader(cfg.article));
    }
    var figure = buildVideoFigure(cfg);
    root.appendChild(figure);

    ensureTickerStyles();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var h = figure.querySelector('[data-story-ticker="headline"]');
        var q = figure.querySelector('[data-story-ticker="quote"]');
        if (h) refreshTickerAnimatedLine(h, h.textContent);
        if (q) refreshTickerAnimatedLine(q, q.textContent);
      });
    });

    clearStoryHeroTicker = startOverlayRotation(figure, cfg.overlay);
    root.setAttribute("data-story-hero-mounted", "true");
  }

  window.mountStoryHero = mountStoryHero;
  window.STORY_HERO_DEFAULTS = DEFAULT_CONFIG;

  function autoMount() {
    if (!document.getElementById("story-hero-root")) return;
    if (document.getElementById("story-hero-root").getAttribute("data-skip-auto")) return;
    mountStoryHero();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoMount);
  } else {
    autoMount();
  }

  document.addEventListener("shopify:section:load", function () {
    autoMount();
  });
})();
